import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  Timestamp,
  writeBatch,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { NavNode, PageType, BlockType } from '../types/nav';

const COLLECTION = 'demo_portal_nav';
const colRef = collection(db, COLLECTION);

export async function getNavTree(): Promise<NavNode[]> {
  const q = query(colRef, orderBy('order', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as NavNode));
}

export async function addSection(
  title: string,
  icon: string,
  uid: string,
  nextOrder: number,
): Promise<string> {
  const now = Timestamp.now();
  const ref = await addDoc(colRef, {
    type: 'section',
    title,
    icon,
    parentId: null,
    order: nextOrder,
    createdBy: uid,
    createdAt: now,
    updatedAt: now,
    updatedBy: uid,
  });
  return ref.id;
}

export async function addPage(
  parentId: string,
  title: string,
  url: string,
  pageType: PageType,
  uid: string,
  nextOrder: number,
  cardId?: string,
  blockId?: string,
): Promise<string> {
  const now = Timestamp.now();
  const data: Record<string, unknown> = {
    type: 'page',
    title,
    parentId,
    url,
    pageType,
    order: nextOrder,
    createdBy: uid,
    createdAt: now,
    updatedAt: now,
    updatedBy: uid,
  };
  if (cardId) data.cardId = cardId;
  if (blockId) data.blockId = blockId;
  const ref = await addDoc(colRef, data);
  return ref.id;
}

export async function updateNode(
  id: string,
  updates: Partial<Pick<NavNode, 'title' | 'icon' | 'url' | 'pageType' | 'order'>>,
  uid: string,
): Promise<void> {
  const ref = doc(db, COLLECTION, id);
  await updateDoc(ref, {
    ...updates,
    updatedAt: Timestamp.now(),
    updatedBy: uid,
  });
}

export async function deleteNode(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id));
}

export async function reorderNodes(
  nodeIds: string[],
  uid: string,
): Promise<void> {
  const batch = writeBatch(db);
  const now = Timestamp.now();
  nodeIds.forEach((id, index) => {
    batch.update(doc(db, COLLECTION, id), {
      order: index,
      updatedAt: now,
      updatedBy: uid,
    });
  });
  await batch.commit();
}

export async function addBlock(
  parentId: string,
  blockType: BlockType,
  title: string,
  uid: string,
  order: number,
): Promise<string> {
  const now = Timestamp.now();
  const ref = await addDoc(colRef, {
    type: 'block',
    blockType,
    title,
    parentId,
    order,
    createdBy: uid,
    createdAt: now,
    updatedAt: now,
    updatedBy: uid,
  });
  return ref.id;
}

export async function movePageToBlock(
  pageId: string,
  blockId: string | null,
  uid: string,
): Promise<void> {
  const ref = doc(db, COLLECTION, pageId);
  const now = Timestamp.now();
  if (blockId === null) {
    await updateDoc(ref, {
      blockId: deleteField(),
      updatedAt: now,
      updatedBy: uid,
    });
  } else {
    await updateDoc(ref, {
      blockId,
      updatedAt: now,
      updatedBy: uid,
    });
  }
}
